import React, { useEffect, useState } from 'react';
import ContentSection from '../common/ContentSection/ContentSection';
import { useTranslation } from 'react-i18next';
import { Dropdown } from 'primereact/dropdown';
import { ComparisonDetailMembersDataType, VotingCardDataType } from '../../types';
import { TabPanel, TabView } from 'primereact/tabview';
import DataCards from '../dataCards/DataCards/DataCards';
import { useCouncilPath } from '../../hooks/useCouncilPath';
import { VotingDataCard } from '../dataCards/dataCardsTemplates/VotingDataCard';
import type { SessionVoteDetail } from '../../api';
import './VotingComparison.css';
import { TFunction } from 'i18next';

const transformDataToVotingDataType = (data: SessionVoteDetail[], t: TFunction) => {
    return data.map((item) => {
        return {
            voteId: item.id_hlasovani,
            voteNumber: item.cislo_hlasovani,
            meetingNumber: item.cislo_zasedani,
            voteDescription: item.predmet_hlasovani
                ? item.predmet_hlasovani
                : `* ${t('votingWithoutDescription', { ns: 'votings' })} *`,
            voteResult: item.vysledek,
            secret: false,
            valid: true,
            procedural: false
        };
    });
};

type Props = {
    comparison: ComparisonDetailMembersDataType['comparison'];
};

const VotingComparison = ({ comparison }: Props) => {
    const { t } = useTranslation(['analyses', 'translation']);
    const firstSession = comparison?.zasedani[0] ?? null;
    const [selectedSession, setSelectedSession] = useState(firstSession);
    const councilPath = useCouncilPath();

    useEffect(() => {
        setSelectedSession(firstSession);
    }, [firstSession]);

    const sessionDropdown = (
        <div className='dropdown'>
            <h6>{t('comparison.VotingComparison.selectMeeting')}</h6>
            <Dropdown
                inputId='compareSessionSelection'
                value={selectedSession}
                options={comparison?.zasedani}
                onChange={(e) => setSelectedSession(e.value)}
                optionLabel='name'
            />
        </div>
    );

    const agreementData = selectedSession?.name
        ? transformDataToVotingDataType(comparison?.data[selectedSession.name]?.['shoda'] ?? [], t)
        : [];
    const disagreementData = selectedSession?.name
        ? transformDataToVotingDataType(comparison?.data[selectedSession.name]?.['neshoda'] ?? [], t)
        : [];

    const commonDataCardsProps = {
        renderCardCallback: (cardData: VotingCardDataType) => <VotingDataCard data={cardData} />,
        getCardUrl: (item: VotingCardDataType) => councilPath('hlasovani', item.voteId),
        getUniqueId: (item: VotingCardDataType) => item.voteId
    };
    return (
        <ContentSection
            level={3}
            title={t('comparison.VotingComparison.councillorsVotings')}
            subtitle={t('comparison.VotingComparison.councillorsVotingsTooltip')}
            titleElement={sessionDropdown}
            className='voting-comparison-section dashboard-card'
        >
            <TabView>
                <TabPanel header={t('comparison.VotingComparison.councillorsAgreement')}>
                    {agreementData.length === 0 ? (
                        t('comparison.VotingComparison.noVotingsFound')
                    ) : (
                        <DataCards {...commonDataCardsProps} cardsData={agreementData} />
                    )}
                </TabPanel>
                <TabPanel header={t('comparison.VotingComparison.councillorsDisagreement')}>
                    {disagreementData.length === 0 ? (
                        t('comparison.VotingComparison.noVotingsFound')
                    ) : (
                        <DataCards {...commonDataCardsProps} cardsData={disagreementData} />
                    )}
                </TabPanel>
            </TabView>
        </ContentSection>
    );
};

export default VotingComparison;
